import classNames from "classnames";
import { createContext, useEffect, useState } from "react";
import { AuthContext } from "../AuthWrapper";
import { useOutletContext } from "react-router-dom";
import { IStudent } from "../../api/client";
import { Container } from "../Container";



// Context for managing form data and actions
declare interface StudentFormContext {
  formData: object;
  onSubmit: (values: any) => void;
  setFormData: (value: object) => void;
  setPage: (page: number) => void;
  isCreating: boolean;
}
export const FormContext = createContext<StudentFormContext>({} as StudentFormContext);

// Page structure definition for the form
declare interface Page {
  node: React.ReactNode;
  index: number;
}

// Props for the FormWrapper component
declare interface FormWrapperProps<TEnum> {
  pages: Map<TEnum, Page>;
  width?: 'full' | 'narrow' | undefined;
  hideBackToPortalButton?: boolean;
  hideStepTracker?: boolean;
  pageWrapperClassName?: string;
}
export function FormWrapper<TEnum>({ pages, width, ...props }: FormWrapperProps<TEnum>): JSX.Element {
  const [pageKey, setPage] = useState<TEnum>(0 as TEnum);

  const { student }: AuthContext = useOutletContext();
  const [isCreating, setIsCreating] = useState<boolean>(!student);
  const [formData, setFormData] = useState<IStudent>(student || {});

  useEffect(() => {
    setIsCreating(!student);

    if (student) {
      setFormData(student);
    }
  }, [student]);

  const page = pages.get(pageKey);
  const pageCount = new Array(...pages.values()).filter(x => x.index >= 0).length;

  const setPageNumber = (val: number) => {
    setPage(val as TEnum);
  };

  // Handler for form submission
  const onSubmit = (values: any) => {
    const data = { ...formData, ...values };
    setFormData(data);
    if (values.submitAction === 'back') {
      setPage((pageKey as number - 1) as TEnum);
    } else {
      if (pageKey === pageCount-1) { // pageKey starts at 0, and pageCount minimum is 1, 
                                     // so if we want to check whether we reached last page (Confirmation page) with pageKey,
                                     // then compare with pageCount-1
        console.error('final submit handler must be registered in form');
        return;
      }
      setPage((pageKey as number + 1) as TEnum);
    }
  }



  const pageWrapperClassName = classNames(
    props.pageWrapperClassName,
    'p-3',
  );

  return (
    <FormContext.Provider value={{ formData, onSubmit, setFormData, setPage: setPageNumber, isCreating }}>
      <Container>
          <div className={pageWrapperClassName}>
            {page?.node}
          </div>
      </Container>
    </FormContext.Provider>
  )
}

// Function to copy data from formData to initialData
export function copyDataToForm(formData: any, initialData: any) {
  Object.keys(formData).filter(k => k in initialData).forEach(k => {
    if (!(k in initialData)) return;
    initialData[k] = formData[k];
  });
  initialData['submitAction'] = 'next';
}

// Function to set only one field as required in initialData
export function setOnlyOneRequired(key: string, initialData: any) {
  Object.keys(initialData).forEach(k => {
    if (initialData[k] === 'required') initialData[k] = '';
  });
  initialData[key] = 'required';
}
